import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type { UserPublic } from "shared/schemas";
import { DEFAULT_LOCALE, type SupportedLocale, isSupportedLocale } from "../i18n/messages.js";
import { ME_QUERY_KEY, patchUserSettings } from "./use-auth.js";

const STORAGE_KEY = "anonymize.language";

export function detectBrowserLocale(navigatorLanguages: readonly string[]): SupportedLocale {
  for (const lang of navigatorLanguages) {
    const short = lang.split("-")[0]?.toLowerCase();
    if (short && isSupportedLocale(short)) return short;
  }
  return DEFAULT_LOCALE;
}

export function readStoredLocale(): SupportedLocale | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v && isSupportedLocale(v) ? v : null;
}

export function storeLocale(locale: SupportedLocale): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, locale);
}

/**
 * Hook for use inside the router: reads :lang param, provides a setter that
 * navigates to the same path under a different locale prefix.
 */
export function useLanguage() {
  const params = useParams<{ lang?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  const current: SupportedLocale = isSupportedLocale(params.lang ?? "")
    ? (params.lang as SupportedLocale)
    : DEFAULT_LOCALE;

  const setLanguage = useCallback(
    (next: SupportedLocale) => {
      storeLocale(next);
      // Rewrite the URL: /:lang/rest → /:next/rest
      const parts = location.pathname.split("/").filter(Boolean);
      if (parts.length > 0 && isSupportedLocale(parts[0] ?? "")) {
        parts[0] = next;
      } else {
        parts.unshift(next);
      }
      navigate(`/${parts.join("/")}${location.search}${location.hash}`);
      const user = qc.getQueryData<UserPublic | null>(ME_QUERY_KEY);
      if (user && user.language !== next) {
        void patchUserSettings(qc, { language: next });
      }
    },
    [navigate, location, qc],
  );

  return { language: current, setLanguage };
}
